import React, { createContext, useContext, useState, useEffect } from "react";

const API_KEY    = process.env.REACT_APP_YOUTUBE_API_KEY;
const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID;

const CACHE_KEY     = "yt_data_cache";
const CACHE_TTL_MS  = 15 * 60 * 1000; // 15 dakika

// Module-level singleton — aynı anda iki bileşen render olsa bile tek fetch
let inflightPromise = null;

async function fetchYouTubeData() {
  if (inflightPromise) return inflightPromise;

  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    try {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL_MS) return data;
    } catch {
      localStorage.removeItem(CACHE_KEY);
    }
  }

  inflightPromise = fetch(
    `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${CHANNEL_ID}&type=video&order=date&maxResults=10&key=${API_KEY}`
  )
    .then((r) => r.json())
    .then((json) => {
      if (json.error) {
        console.error("[YouTube] API hatası:", json.error.code, json.error.message);
        return null;
      }

      const items = json.items ?? [];
      const liveItem = items.find((v) => v.snippet.liveBroadcastContent === "live");
      const regularItems = items.filter((v) => v.snippet.liveBroadcastContent === "none");

      const liveVideo = liveItem
        ? {
            videoId: liveItem.id.videoId,
            title: decodeHtml(liveItem.snippet.title),
            thumbnail: liveItem.snippet.thumbnails?.high?.url ?? liveItem.snippet.thumbnails?.default?.url ?? null,
          }
        : null;

      const liveVideoCard = liveItem ? mapVideo(liveItem, true) : null;
      const videos = liveVideoCard
        ? [liveVideoCard, ...regularItems.slice(0, 2).map((v) => mapVideo(v, false))]
        : regularItems.slice(0, 3).map((v) => mapVideo(v, false));

      const result = { liveVideo, videos };
      localStorage.setItem(CACHE_KEY, JSON.stringify({ data: result, timestamp: Date.now() }));
      return result;
    })
    .catch((err) => {
      console.error("[YouTube] Fetch hatası:", err);
      return null;
    })
    .finally(() => {
      inflightPromise = null;
    });

  return inflightPromise;
}

function mapVideo(item, isLive = false) {
  const { snippet } = item;
  return {
    id: item.id.videoId,
    title: decodeHtml(snippet.title),
    description: decodeHtml(snippet.description),
    thumbnail: snippet.thumbnails?.high?.url ?? snippet.thumbnails?.default?.url ?? null,
    publishedAt: snippet.publishedAt,
    isLive,
    isPlaceholder: false,
  };
}

function decodeHtml(str) {
  if (!str) return str;
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
}

// ─── CONTEXT ──────────────────────────────────────────────────────────────────

const YouTubeContext = createContext({ liveVideo: null, videos: [], loading: false });

export function YouTubeProvider({ children }) {
  const isConfigured = Boolean(API_KEY && CHANNEL_ID);
  const [state, setState] = useState({ liveVideo: null, videos: [], loading: false });

  useEffect(() => {
    if (!isConfigured) return;
    setState((s) => ({ ...s, loading: true }));
    fetchYouTubeData().then((data) => {
      setState({
        liveVideo: data?.liveVideo ?? null,
        videos:    data?.videos    ?? [],
        loading:   false,
      });
    });
  }, [isConfigured]);

  return <YouTubeContext.Provider value={state}>{children}</YouTubeContext.Provider>;
}

export function useYouTube()    { return useContext(YouTubeContext); }
export function useLiveStream() { const { liveVideo, loading } = useContext(YouTubeContext); return { liveVideo, loading }; }
